// src/components/SmartFilterBar.js
// Smart, organized filter bar with progressive disclosure
import React, { useState, useRef, useEffect } from 'react';
import { 
    RiFilterLine, 
    RiCloseLine, 
    RiArrowDownSLine,
    RiAddLine
} from 'react-icons/ri';
import { DateRangeFilter, DRIFilter } from './FilterControls';
import apollo from '../styles/apolloTheme';

/**
 * ActiveFilterChip - Displays an active filter as a removable chip
 */
const ActiveFilterChip = ({ label, value, onRemove }) => (
    <div 
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full"
        style={{
            backgroundColor: apollo.color.selectedBg,
            color: apollo.color.primary,
            border: `1px solid ${apollo.color.primary}`,
        }}
    >
        <span style={{ color: apollo.color.textSecondary }}>{label}:</span>
        <span className="font-semibold">{value}</span>
        <button
            onClick={onRemove}
            className="ml-0.5 p-0.5 rounded-full hover:bg-white/50 transition-colors"
            style={{ color: apollo.color.primary }}
        >
            <RiCloseLine className="w-3 h-3" />
        </button>
    </div>
);

/**
 * MoreFiltersDropdown - Dropdown to add additional filters
 */
const MoreFiltersDropdown = ({ 
    filterGroups,
    activeFilters,
    onFilterChange,
    filterConfigs 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [openGroup, setOpenGroup] = useState(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setOpenGroup(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const hasActiveInGroup = (groupId) => {
        const group = filterGroups.find(g => g.id === groupId);
        if (!group) return false;
        return group.filters.some(f => {
            const value = activeFilters[f.key];
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === 'boolean') return value;
            if (typeof value === 'number') return value > (f.min || 0);
            return value && value !== '';
        });
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all"
                style={{
                    backgroundColor: apollo.color.white,
                    borderColor: apollo.color.borderDefault,
                    color: apollo.color.textSecondary,
                }}
                onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = apollo.color.primary;
                    e.currentTarget.style.color = apollo.color.primary;
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = apollo.color.borderDefault;
                    e.currentTarget.style.color = apollo.color.textSecondary;
                }}
            >
                <RiAddLine className="w-4 h-4" />
                More Filters
                <RiArrowDownSLine 
                    className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <div 
                    className="absolute left-0 top-full mt-2 w-72 rounded-xl overflow-hidden z-50"
                    style={{ 
                        backgroundColor: apollo.color.white,
                        boxShadow: apollo.shadow.lg,
                        border: `1px solid ${apollo.color.borderDefault}`,
                    }}
                >
                    {/* Header */}
                    <div 
                        className="px-4 py-2.5"
                        style={{ 
                            backgroundColor: apollo.color.bgSecondary,
                            borderBottom: `1px solid ${apollo.color.borderSubtle}`,
                        }}
                    >
                        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: apollo.color.textSecondary }}>
                            Filter Categories
                        </span>
                    </div>

                    {/* Filter Groups */}
                    <div className="max-h-80 overflow-y-auto">
                        {filterGroups.map((group) => {
                            const isGroupOpen = openGroup === group.id;
                            const hasActive = hasActiveInGroup(group.id);
                            
                            return (
                                <div key={group.id}>
                                    <button
                                        onClick={() => setOpenGroup(isGroupOpen ? null : group.id)}
                                        className="w-full px-4 py-2.5 flex items-center justify-between text-sm transition-colors"
                                        style={{ 
                                            color: apollo.color.textPrimary,
                                            backgroundColor: isGroupOpen ? apollo.color.bgSecondary : 'transparent',
                                        }}
                                        onMouseOver={(e) => {
                                            if (!isGroupOpen) e.currentTarget.style.backgroundColor = apollo.color.bgSecondary;
                                        }}
                                        onMouseOut={(e) => {
                                            if (!isGroupOpen) e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium">{group.label}</span>
                                            {hasActive && (
                                                <span 
                                                    className="w-2 h-2 rounded-full"
                                                    style={{ backgroundColor: apollo.color.primary }}
                                                />
                                            )}
                                        </div>
                                        <RiArrowDownSLine 
                                            className={`w-4 h-4 transition-transform ${isGroupOpen ? 'rotate-180' : ''}`}
                                            style={{ color: apollo.color.iconGray }}
                                        />
                                    </button>
                                    
                                    {isGroupOpen && (
                                        <div 
                                            className="px-4 py-3 space-y-3"
                                            style={{ 
                                                backgroundColor: apollo.color.bgSecondary,
                                                borderTop: `1px solid ${apollo.color.borderSubtle}`,
                                            }}
                                        >
                                            {group.filters.map((filter) => {
                                                const config = filterConfigs[filter.key];
                                                if (!config) return null;
                                                
                                                return (
                                                    <div key={filter.key} className="flex flex-wrap items-center gap-2">
                                                        {config.render(activeFilters, onFilterChange)}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * CategoryFilterDropdown - A dropdown button that shows filters for a specific category
 * Used in collapsible mode for organizing filters into groups (Overview pages only)
 */
const CategoryFilterDropdown = ({
    label,
    group,
    filters,
    filterConfigs,
    activeFilters,
    onFilterChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Check if any filter in this group is active
    const hasActiveFilter = group.filters.some(f => {
        const value = activeFilters[f.key];
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'boolean') return value;
        if (typeof value === 'number') return value > (f.min || 0);
        return value && value !== '';
    });

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all"
                style={{
                    backgroundColor: hasActiveFilter ? apollo.color.selectedBg : apollo.color.white,
                    borderColor: hasActiveFilter ? apollo.color.primary : apollo.color.borderDefault,
                    color: hasActiveFilter ? apollo.color.primary : apollo.color.textPrimary,
                }}
                onMouseOver={(e) => {
                    if (!hasActiveFilter) {
                        e.currentTarget.style.borderColor = apollo.color.primary;
                        e.currentTarget.style.color = apollo.color.primary;
                    }
                }}
                onMouseOut={(e) => {
                    if (!hasActiveFilter) {
                        e.currentTarget.style.borderColor = apollo.color.borderDefault;
                        e.currentTarget.style.color = apollo.color.textPrimary;
                    }
                }}
            >
                {label}
                {hasActiveFilter && (
                    <span 
                        className="w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: apollo.color.primary }}
                    />
                )}
                <RiArrowDownSLine 
                    className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <div 
                    className="absolute left-0 top-full mt-1 min-w-[280px] rounded-lg z-50"
                    style={{ 
                        backgroundColor: apollo.color.white,
                        boxShadow: apollo.shadow.lg,
                        border: `1px solid ${apollo.color.borderDefault}`,
                    }}
                >
                    <div className="p-3 flex flex-wrap items-center gap-2">
                        {group.filters.map((filter) => {
                            const config = filterConfigs[filter.key];
                            if (!config) return null;
                            return (
                                <React.Fragment key={filter.key}>
                                    {config.render(filters, onFilterChange)}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * SmartFilterBar - Intelligent, organized filter bar
 * 
 * Features:
 * - Primary row with essential filters (Date, DRI)
 * - Active filter chips for quick visibility
 * - "More Filters" dropdown with categorized options (default mode)
 * - All filters inline (collapsible mode when expanded)
 * - Progressive disclosure for complexity management
 */
const SmartFilterBar = ({
    // Date range
    dateRange,
    onDateRangeChange,
    
    // DRI filter
    dris = [],
    selectedDris = [],
    onDriChange,
    loadingDris = false,
    driDisplayFormatter = null,  // Optional function to format DRI display (e.g., email to name)
    
    // Filter groups configuration
    filterGroups = [],
    
    // Current filter values
    filters = {},
    
    // Filter change handlers
    onFilterChange,
    
    // Filter configurations for rendering
    filterConfigs = {},
    
    // Active filter display
    activeFilterChips = [],
    
    // Clear all
    onClearAll,
    hasActiveFilters = false,
    
    // Variant
    variant = 'default', // 'default' | 'collapsible'
    isExpanded = true,
}) => {
    // Count active filters in each group
    const activeFilterCount = activeFilterChips.length + selectedDris.length;
    
    // For collapsible variant with EntityNavigation integration
    if (variant === 'collapsible' && !isExpanded) {
        return null;
    }

    // For collapsible variant
    // - Subpages: show filters inline directly
    // - Overview pages (has 'priority' group or 3+ groups): use category dropdowns
    if (variant === 'collapsible') {
        const hasPriorityMatrix = filterGroups.some(g => g.id === 'priority');
        const isOverviewPage = hasPriorityMatrix || filterGroups.length >= 3;
        const isSubpage = !isOverviewPage;
        
        return (
            <div 
                className="-mx-4 md:-mx-8 px-4 md:px-8 py-2.5 mb-2"
                style={{ 
                    backgroundColor: apollo.color.bgSecondary,
                    borderBottom: `1px solid ${apollo.color.borderDefault}`,
                }}
            >
                {/* All Filter Controls in One Row */}
                <div className="flex flex-wrap items-center gap-2">
                    {/* Date Range Filter */}
                    <DateRangeFilter
                        startDate={dateRange?.start}
                        endDate={dateRange?.end}
                        onChange={onDateRangeChange}
                    />

                    {/* DRI Filter */}
                    <DRIFilter
                        dris={dris}
                        selectedDris={selectedDris}
                        onDriChange={onDriChange}
                        loading={loadingDris}
                        displayFormatter={driDisplayFormatter}
                    />

                    {/* Subpages: Show filters inline directly */}
                    {isSubpage && filterGroups.map((group) => (
                        <React.Fragment key={group.id}>
                            {group.filters.map((filter) => {
                                const config = filterConfigs[filter.key];
                                if (!config) return null;
                                return (
                                    <React.Fragment key={filter.key}>
                                        {config.render(filters, onFilterChange)}
                                    </React.Fragment>
                                );
                            })}
                        </React.Fragment>
                    ))}

                    {/* Overview pages: Use category dropdowns */}
                    {!isSubpage && filterGroups.map((group) => (
                        <CategoryFilterDropdown
                            key={group.id}
                            label={group.label}
                            group={group}
                            filters={filters}
                            filterConfigs={filterConfigs}
                            activeFilters={filters}
                            onFilterChange={onFilterChange}
                        />
                    ))}

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Clear All Button */}
                    {onClearAll && hasActiveFilters && (
                        <button
                            onClick={onClearAll}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all"
                            style={{ 
                                color: apollo.color.linkDefault,
                                backgroundColor: 'transparent',
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = apollo.color.white;
                                e.currentTarget.style.color = apollo.color.linkHover;
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = apollo.color.linkDefault;
                            }}
                        >
                            <RiCloseLine className="w-3.5 h-3.5" />
                            Clear all
                        </button>
                    )}
                </div>

                {/* Active Filter Chips Row - Only show when there are active filters */}
                {activeFilterChips.length > 0 && (
                    <div 
                        className="mt-2 pt-2 flex flex-wrap items-center gap-2"
                        style={{ borderTop: `1px solid ${apollo.color.borderSubtle}` }}
                    >
                        <span className="text-xs font-medium mr-1" style={{ color: apollo.color.textSecondary }}>
                            Active:
                        </span>
                        {activeFilterChips.map((chip, index) => (
                            <ActiveFilterChip
                                key={`${chip.label}-${index}`}
                                label={chip.label}
                                value={chip.value}
                                onRemove={chip.onRemove}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // Default variant (with More Filters dropdown)
    return (
        <div 
            className="mb-4 py-3 px-4"
            style={{ 
                backgroundColor: apollo.color.white,
                border: `1px solid ${apollo.color.borderDefault}`,
                borderRadius: apollo.radius.lg,
                boxShadow: apollo.shadow.xs,
            }}
        >
            {/* Primary Row: Essential Filters */}
            <div className="flex flex-wrap items-center gap-3">
                {/* Filter Icon Label */}
                <div 
                    className="flex items-center gap-2 py-1 px-2 flex-shrink-0"
                    style={{ 
                        color: apollo.color.textSecondary,
                        backgroundColor: apollo.color.bgSecondary,
                        borderRadius: apollo.radius.default,
                    }}
                >
                    <RiFilterLine className="w-4 h-4" />
                    <span className="text-sm font-medium">Filters</span>
                    {activeFilterCount > 0 && (
                        <span 
                            className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full"
                            style={{ 
                                backgroundColor: apollo.color.primary,
                                color: apollo.color.white,
                            }}
                        >
                            {activeFilterCount}
                        </span>
                    )}
                </div>

                {/* Divider */}
                <div className="w-px h-6" style={{ backgroundColor: apollo.color.borderDefault }} />

                {/* Date Range Filter - Always Primary */}
                <DateRangeFilter
                    startDate={dateRange?.start}
                    endDate={dateRange?.end}
                    onChange={onDateRangeChange}
                />

                {/* DRI Filter - Always Primary */}
                <DRIFilter
                    dris={dris}
                    selectedDris={selectedDris}
                    onDriChange={onDriChange}
                    loading={loadingDris}
                    displayFormatter={driDisplayFormatter}
                />

                {/* More Filters Dropdown */}
                {filterGroups.length > 0 && (
                    <MoreFiltersDropdown
                        filterGroups={filterGroups}
                        activeFilters={filters}
                        onFilterChange={onFilterChange}
                        filterConfigs={filterConfigs}
                    />
                )}

                {/* Spacer */}
                <div className="flex-1" />

                {/* Clear All Button */}
                {onClearAll && hasActiveFilters && (
                    <button
                        onClick={onClearAll}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all"
                        style={{ 
                            color: apollo.color.linkDefault,
                            backgroundColor: 'transparent',
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = apollo.color.bgSecondary;
                            e.currentTarget.style.color = apollo.color.linkHover;
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = apollo.color.linkDefault;
                        }}
                    >
                        <RiCloseLine className="w-3.5 h-3.5" />
                        Clear all
                    </button>
                )}
            </div>

            {/* Active Filter Chips Row - Only show when there are active filters */}
            {activeFilterChips.length > 0 && (
                <div 
                    className="mt-2.5 pt-2.5 flex flex-wrap items-center gap-2"
                    style={{ borderTop: `1px solid ${apollo.color.borderSubtle}` }}
                >
                    <span className="text-xs font-medium mr-1" style={{ color: apollo.color.textSecondary }}>
                        Active:
                    </span>
                    {activeFilterChips.map((chip, index) => (
                        <ActiveFilterChip
                            key={`${chip.label}-${index}`}
                            label={chip.label}
                            value={chip.value}
                            onRemove={chip.onRemove}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default SmartFilterBar;


